import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const SAP_BLUE = [0, 112, 242];
const SAP_DARK = [15, 23, 42];
const SAP_SLATE = [100, 116, 139];
const SAP_BG = [244, 245, 247];
const SAP_BG_LIGHT = [240, 247, 255];
const SAP_BORDER = [226, 232, 240];
const PALETTE = [
  [0, 112, 242],
  [0, 176, 255],
  [30, 41, 59],
  [100, 116, 139],
  [14, 165, 233],
  [148, 163, 184],
];

const tryFmt = (n) =>
  new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(n || 0);
const numFmt = (n) =>
  new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 }).format(n || 0);

const stripMd = (s) =>
  (s || "").replace(/\*\*/g, "").replace(/^#+\s*/gm, "").replace(/^[-*]\s+/gm, "• ");

/**
 * Build an enterprise-grade PDF report from a chat query result.
 * @param {Object} data - { question, answer, filter, summary, rows }
 */
export function generateAnalysisPdf({ question, answer, filter, summary, rows }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 40;
  let y = 0;

  // ===== Header band =====
  doc.setFillColor(...SAP_BLUE);
  doc.rect(0, 0, W, 90, "F");
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(M, 28, 36, 36, 6, 6, "F");
  doc.setFontSize(20);
  doc.setTextColor(...SAP_BLUE);
  doc.setFont("helvetica", "bold");
  doc.text("TM", M + 18, 52, { align: "center" });

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("SAP TM ANALYTICS", M + 50, 42);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Taşıma Yönetimi Analiz Raporu", M + 50, 62);

  const stamp = new Date().toLocaleString("tr-TR");
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(`Oluşturulma: ${stamp}`, W - M, 42, { align: "right" });
  doc.text("AI Asistan tarafından üretildi", W - M, 56, { align: "right" });

  y = 120;

  // ===== Question card =====
  doc.setFillColor(...SAP_BG);
  doc.setDrawColor(...SAP_BORDER);
  doc.roundedRect(M, y, W - 2 * M, 60, 6, 6, "FD");
  doc.setTextColor(...SAP_SLATE);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text("SORU", M + 14, y + 18);
  doc.setTextColor(...SAP_DARK);
  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  const qLines = doc.splitTextToSize(question, W - 2 * M - 28);
  doc.text(qLines, M + 14, y + 36);
  y += Math.max(60, 36 + qLines.length * 14) + 16;

  // ===== Filter chips row =====
  const chips = [];
  if (filter?.start_date && filter?.end_date) chips.push(`${filter.start_date} → ${filter.end_date}`);
  if (filter?.carrier_name) chips.push(`Nakliyeci: ${filter.carrier_name}`);
  if (filter?.source_name) chips.push(`Lokasyon: ${filter.source_name}`);
  if (filter?.mode) chips.push(`Mod: ${filter.mode}`);
  if (chips.length === 0) chips.push("Filtre yok (tüm veri)");

  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...SAP_SLATE);
  doc.text("UYGULANAN FİLTRE", M, y);
  y += 12;
  let cx = M;
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  chips.forEach((c) => {
    const w = doc.getTextWidth(c) + 16;
    if (cx + w > W - M) { cx = M; y += 22; }
    doc.setFillColor(...SAP_BG_LIGHT);
    doc.setDrawColor(186, 230, 253);
    doc.roundedRect(cx, y, w, 18, 9, 9, "FD");
    doc.setTextColor(...SAP_BLUE);
    doc.text(c, cx + 8, y + 12);
    cx += w + 6;
  });
  y += 32;

  // ===== KPI cards =====
  const kpis = [
    { label: "TOPLAM SEVKİYAT", value: numFmt(summary.total_shipments) },
    { label: "TOPLAM MALİYET", value: tryFmt(summary.total_cost) },
    { label: "ORT. MALİYET", value: tryFmt(summary.avg_cost) },
    { label: "ZAMANINDA", value: `%${numFmt(summary.on_time_pct)}` },
  ];
  const cardW = (W - 2 * M - 18) / 4;
  kpis.forEach((k, i) => {
    const x = M + i * (cardW + 6);
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...SAP_BORDER);
    doc.roundedRect(x, y, cardW, 56, 6, 6, "FD");
    doc.setTextColor(...SAP_SLATE);
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.text(k.label, x + 10, y + 16);
    doc.setTextColor(...SAP_DARK);
    doc.setFontSize(13);
    doc.text(k.value, x + 10, y + 38);
  });
  y += 76;

  // ===== AI answer =====
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...SAP_SLATE);
  doc.text("AI ANALİZ CEVABI", M, y);
  y += 14;
  doc.setFillColor(...SAP_BG_LIGHT);
  doc.setDrawColor(186, 230, 253);
  // Pre-calc text height
  const ansLines = doc.splitTextToSize(stripMd(answer), W - 2 * M - 28);
  const ansH = ansLines.length * 12 + 24;
  doc.roundedRect(M, y, W - 2 * M, ansH, 6, 6, "FD");
  // Accent bar
  doc.setFillColor(...SAP_BLUE);
  doc.rect(M, y, 3, ansH, "F");
  doc.setTextColor(...SAP_DARK);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(ansLines, M + 14, y + 16);
  y += ansH + 18;

  // ===== Carrier breakdown horizontal bar chart =====
  const carrierEntries = Object.entries(summary.by_carrier || {})
    .sort(([, a], [, b]) => b.cost - a.cost)
    .slice(0, 6);

  if (carrierEntries.length) {
    if (y + 30 + carrierEntries.length * 22 > H - 60) {
      doc.addPage(); y = M;
    }
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...SAP_DARK);
    doc.text("Nakliyeci Bazında Maliyet Kırılımı", M, y);
    y += 14;
    const maxCost = Math.max(...carrierEntries.map(([, v]) => v.cost));
    const labelW = 110;
    const barAreaW = W - 2 * M - labelW - 80;
    carrierEntries.forEach(([name, v], i) => {
      const barW = (v.cost / maxCost) * barAreaW;
      doc.setTextColor(...SAP_DARK);
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.text(name, M, y + 12);
      doc.setFillColor(...PALETTE[i % PALETTE.length]);
      doc.roundedRect(M + labelW, y + 4, Math.max(barW, 2), 12, 2, 2, "F");
      doc.setTextColor(...SAP_DARK);
      doc.setFont("helvetica", "bold");
      doc.text(`${tryFmt(v.cost)} (${v.count})`, M + labelW + barW + 6, y + 13);
      y += 20;
    });
    y += 12;
  }

  // ===== Location breakdown table =====
  const locEntries = Object.entries(summary.by_location || {})
    .sort(([, a], [, b]) => b.cost - a.cost)
    .slice(0, 8);
  if (locEntries.length) {
    if (y + 30 + locEntries.length * 18 > H - 60) {
      doc.addPage(); y = M;
    }
    autoTable(doc, {
      startY: y,
      head: [["Kaynak Lokasyon", "Sevkiyat", "Toplam Maliyet"]],
      body: locEntries.map(([name, v]) => [name, numFmt(v.count), tryFmt(v.cost)]),
      theme: "grid",
      headStyles: { fillColor: SAP_BLUE, textColor: 255, fontStyle: "bold", fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: SAP_DARK },
      alternateRowStyles: { fillColor: SAP_BG },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right", fontStyle: "bold" } },
      margin: { left: M, right: M },
      didDrawPage: () => {},
    });
    y = doc.lastAutoTable.finalY + 16;
  }

  // ===== Mode breakdown table =====
  const modeEntries = Object.entries(summary.by_mode || {}).sort(([, a], [, b]) => b.cost - a.cost);
  if (modeEntries.length) {
    if (y + 30 + modeEntries.length * 18 > H - 60) {
      doc.addPage(); y = M;
    }
    autoTable(doc, {
      startY: y,
      head: [["Taşıma Modu", "Sevkiyat", "Toplam Maliyet"]],
      body: modeEntries.map(([name, v]) => [name, numFmt(v.count), tryFmt(v.cost)]),
      theme: "grid",
      headStyles: { fillColor: SAP_BLUE, textColor: 255, fontStyle: "bold", fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: SAP_DARK },
      alternateRowStyles: { fillColor: SAP_BG },
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right", fontStyle: "bold" } },
      margin: { left: M, right: M },
    });
    y = doc.lastAutoTable.finalY + 16;
  }

  // ===== Shipment detail table (new page) =====
  if (rows?.length) {
    doc.addPage();
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...SAP_DARK);
    doc.text(`Sevkiyat Detayları (${rows.length} kayıt)`, M, M);
    autoTable(doc, {
      startY: M + 16,
      head: [["TM ID", "Tarih", "Kaynak", "Nakliyeci", "Mod", "Ton", "Maliyet", "Durum"]],
      body: rows.slice(0, 200).map((r) => [
        r.id,
        r.date.slice(0, 10),
        r.source_name,
        r.carrier_name,
        r.mode,
        numFmt(r.weight_tons),
        tryFmt(r.cost_try),
        r.status,
      ]),
      theme: "grid",
      headStyles: { fillColor: SAP_BLUE, textColor: 255, fontStyle: "bold", fontSize: 8 },
      bodyStyles: { fontSize: 7.5, textColor: SAP_DARK },
      alternateRowStyles: { fillColor: SAP_BG },
      columnStyles: { 5: { halign: "right" }, 6: { halign: "right", fontStyle: "bold" } },
      margin: { left: M, right: M },
    });
    if (rows.length > 200) {
      const fy = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(8);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(...SAP_SLATE);
      doc.text(`* İlk 200 satır gösterildi. Toplam ${rows.length} kayıt için Excel raporunu kullanın.`, M, fy);
    }
  }

  // ===== Footer (page numbers) =====
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setDrawColor(...SAP_BORDER);
    doc.line(M, H - 30, W - M, H - 30);
    doc.setTextColor(...SAP_SLATE);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("SAP TM Analytics · AI Asistan Raporu", M, H - 16);
    doc.text(`Sayfa ${p} / ${pageCount}`, W - M, H - 16, { align: "right" });
  }

  const ts = new Date().toISOString().slice(0, 10);
  doc.save(`sap-tm-analiz-${ts}.pdf`);
}
